import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Phone, Mail, MapPin, Wrench } from "lucide-react";
import { toast } from "sonner";
import EditTechnicianDialog from "@/components/EditTechnicianDialog";

async function fetchTechnician(id: string) {
  const { data, error } = await supabase
    .from("technicians")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

export default function TechnicianProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = React.useState(false);

  const { data: tech, isLoading, error } = useQuery({
    queryKey: ["technician", id],
    queryFn: () => fetchTechnician(id!),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const { error } = await supabase
        .from("technicians")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["technician", id] });
      queryClient.invalidateQueries({ queryKey: ["technicians-list"] });
      toast.success("Status updated!");
    },
    onError: () => toast.error("Failed to update status."),
  });

  if (isLoading) return <div className="p-12 text-center">Loading…</div>;
  if (error || !tech) return <div className="p-12 text-center text-destructive">Technician not found.</div>;

  const statusColor = tech.status === "active" ? "bg-teal-100 text-teal-700"
    : tech.status === "on_leave" ? "bg-yellow-100 text-yellow-700"
    : "bg-slate-100 text-slate-500";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Card>
          <CardHeader className="flex-row items-center gap-6 pb-2">
            <Avatar className="h-20 w-20 border">
              {tech.photo_url ? <AvatarImage src={tech.photo_url} alt={tech.name} /> : <AvatarFallback className="text-2xl">{tech.name?.slice(0, 2).toUpperCase()}</AvatarFallback>}
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-2xl">{tech.name}</CardTitle>
              <Badge className={`mt-2 ${statusColor}`}>{tech.status}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="flex items-center gap-2 text-slate-700">
              <Phone className="h-4 w-4 text-muted-foreground" />
              {tech.phone ? <a href={`tel:${tech.phone}`} className="underline">{tech.phone}</a> : <span className="text-muted-foreground">N/A</span>}
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="h-4 w-4 text-muted-foreground" />
              {tech.email ? <a href={`mailto:${tech.email}`} className="underline">{tech.email}</a> : <span className="text-muted-foreground">N/A</span>}
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {tech.current_location_lat && tech.current_location_lng
                ? `${tech.current_location_lat}, ${tech.current_location_lng}`
                : <span className="text-muted-foreground">Location not set</span>}
            </div>
            <div className="flex items-start gap-2 text-slate-700">
              <Wrench className="h-4 w-4 text-muted-foreground mt-1" />
              <span>
                {Array.isArray(tech.specializations) && tech.specializations.length
                  ? tech.specializations.join(", ")
                  : <span className="text-muted-foreground">No specializations</span>}
              </span>
            </div>
            <div className="pt-4 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setEditOpen(true)}>Edit</Button>
              {tech.status === "active" && (
                <Button variant="secondary" onClick={() => statusMutation.mutate("inactive")}>Deactivate</Button>
              )}
              {tech.status === "inactive" && (
                <Button variant="default" onClick={() => statusMutation.mutate("active")}>Activate</Button>
              )}
              {tech.status !== "on_leave" && (
                <Button variant="ghost" onClick={() => statusMutation.mutate("on_leave")}>Set On Leave</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <EditTechnicianDialog open={editOpen} onOpenChange={setEditOpen} technician={tech} />
    </div>
  );
}
